import { LoginPayload } from '@/modules/Auth/types/payload'
import { useForm } from 'react-hook-form'
import { getUserInfo } from '@/modules/Auth/services'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { Alert, Button, TextField } from 'vnetwork-ui'
import { Link } from 'react-router-dom'
import { paths } from '@/utils/path'
import { AxiosError } from 'axios'
import { ErrorResponse } from '@/types/shared'
import EyeIcons from '@/icons/EyeIcons'
import { useState } from 'react'
import EyeOffIcons from '@/icons/EyeOffIcons'
import { useTranslation } from 'react-i18next'
import authSdk from '@/utils/sdk'
import VersionInfo from '../VersionInfo'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export const generateUUIDv4 = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
            const r = (Math.random() * 16) | 0
            const v = c === 'x' ? r : (r & 0x3) | 0x8
            return v.toString(16)
        }
    )
}
const Login = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginPayload>({
        defaultValues: {
            username: '',
            password: '',
        },
    })

    const { setEmployee } = useAuthStore()
    const {
        setPermissions,
        setIsAdmin,
        setCanFilterCustomerEventFollowers,
        setCanDeleteCustomerJourneyNote,
        setCanDeletePlainNotesOnlyCustomerJourney,
        setCanViewLeadOriginCustomers,
    } = usePermissionsStore()
    const { t } = useTranslation()

    const [showPassword, setShowPassword] = useState(false)

    const handleLogin = async (data: LoginPayload) => {
        await authSdk.login(data)

        const { result } = await getUserInfo()
        setEmployee(result?.employee)
        setPermissions(result?.authorization?.permissions)
        setIsAdmin(result?.is_admin ?? false)
        setCanFilterCustomerEventFollowers(
            result?.can_filter_customer_event_followers ?? false
        )
        setCanDeleteCustomerJourneyNote(
            result?.can_delete_customer_journey_note ?? false
        )
        setCanDeletePlainNotesOnlyCustomerJourney(
            result?.can_delete_plain_notes_only_customer_journey ?? false
        )
        setCanViewLeadOriginCustomers(
            result?.can_view_lead_origin_customers ?? false
        )

        localStorage.setItem('session_id', generateUUIDv4())
    }

    const { mutateAsync, isPending, error, isError } = useMutationWithHandler(
        {
            mutationFn: handleLogin,
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            showSuccessToast: false,
            showErrorToast: false,
        }
    )

    const loading = isPending
    const err = error
    const isErr = isError

    const errorMessage =
        (err as AxiosError<ErrorResponse>)?.response?.data?.error_description ||
        err?.message ||
        'Something went wrong'

    const handleVNetworkLogin = async () => {
        try {
            await authSdk.loginWithVNWID()
        } catch (error) {
            console.error('VNETWORK login failed:', error)
        }
    }

    return (
        <div
            className="w-full"
            style={{
                fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif',
            }}
        >
            {/* Carbon Header */}
            <div className="mb-6 md:mb-8">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-black leading-tight mb-2">
                    {t('sign-in-to-datacentral')}
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                    {t('datacentral-subtitle')}
                </p>
            </div>

            {isErr && (
                <Alert
                    size="medium"
                    variant="error"
                    className="mb-4 text-center"
                >
                    {errorMessage}
                </Alert>
            )}

            {/* Carbon Form */}
            <div className="space-y-4 md:space-y-4">
                {/* Authentication Method Selection */}
                <div className="space-y-3 md:space-y-4">
                    <label className="text-sm font-medium text-black block">
                        {t('authentication-method')}
                    </label>

                    {/* Traditional Login Option */}
                    <div className="border border-gray-300 hover:border-blue-600 transition-colors">
                        <div className="p-4">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                </div>
                                <span className="text-sm font-medium text-black">
                                    {t('traditional-login')}
                                </span>
                            </div>

                            <form
                                onSubmit={handleSubmit((data) =>
                                    mutateAsync(data)
                                )}
                            >
                                <div className="space-y-3">
                                    <TextField
                                        {...register('username', {
                                            required: t('email-required'),
                                            pattern: {
                                                message:
                                                    t('wrong-email-format'),
                                                value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                                            },
                                        })}
                                        className="w-full bg-gray-50"
                                        type="text"
                                        placeholder={t('email-placeholder')}
                                        size="medium"
                                        error={!!errors.username}
                                        helperText={errors.username?.message}
                                    />

                                    <TextField
                                        {...register('password', {
                                            required: t('password-required'),
                                        })}
                                        className="w-full bg-gray-50"
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        placeholder={t('password')}
                                        size="medium"
                                        error={!!errors.password}
                                        helperText={errors.password?.message}
                                        endIcon={
                                            !showPassword ? (
                                                <EyeIcons
                                                    onClick={() =>
                                                        setShowPassword(
                                                            !showPassword
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <EyeOffIcons
                                                    onClick={() =>
                                                        setShowPassword(
                                                            !showPassword
                                                        )
                                                    }
                                                />
                                            )
                                        }
                                    />

                                    <div className="flex justify-end">
                                        <Link
                                            to={paths.forgot}
                                            className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                            {t('forgot-password')}
                                        </Link>
                                    </div>

                                    <Button
                                        loading={loading}
                                        disabled={loading}
                                        color="default"
                                        variant="contained"
                                        size="medium"
                                        className="w-full"
                                        type="submit"
                                    >
                                        {t('sign-in')}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* VNETWORK ID Option */}
                    <div className="border border-gray-300 hover:border-red-600 transition-colors">
                        <button
                            onClick={handleVNetworkLogin}
                            disabled={loading}
                            className="w-full h-14 sm:h-16 bg-white hover:bg-gray-50 text-black text-sm font-medium border-0 rounded-none flex justify-start items-center px-3 sm:px-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <div className="flex items-center space-x-3 sm:space-x-4">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 flex items-center justify-center flex-shrink-0">
                                    {loading ? (
                                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <div className="w-4 h-4 sm:w-5 sm:h-5 bg-white"></div>
                                    )}
                                </div>
                                <div className="text-left flex-1 min-w-0">
                                    <div className="text-sm font-medium text-black truncate">
                                        {loading
                                            ? t('connecting')
                                            : 'VNETWORK ID'}
                                    </div>
                                    <div className="text-xs text-gray-600 truncate">
                                        {loading
                                            ? t('please-wait')
                                            : t(
                                                  'sign-in-institutional-account'
                                              )}
                                    </div>
                                </div>
                            </div>
                            <div className="ml-auto flex-shrink-0">
                                <div className="w-4 h-4 border border-gray-400 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Help Text */}
                {/* <div className="bg-gray-100 border border-gray-300 p-3 sm:p-4">
                    <div className="flex items-start space-x-2 sm:space-x-3">
                        <div className="w-4 h-4 bg-gray-500 rounded-full mt-0.5 flex items-center justify-center flex-shrink-0">
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-black">
                                {t('need-help-signing-in')}
                            </p>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                                {t('contact-administrator-help')}
                            </p>
                        </div>
                    </div>
                </div> */}

                {/* Terms Notice */}
                <div className="text-xs text-gray-500 leading-relaxed">
                    {t('terms-notice-prefix')}{' '}
                    <a
                        href="https://www.vnetwork.vn/vi-VN/terms-of-service/"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-700 underline"
                    >
                        {t('terms-of-service')}
                    </a>{' '}
                    {t('and')}{' '}
                    <a
                        href="https://www.vnetwork.vn/vi-VN/privacy-policy/"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-700 underline"
                    >
                        {t('privacy-policy')}
                    </a>
                    .
                </div>

                <VersionInfo align="left" />
            </div>
        </div>
    )
}

export default Login
